import { useEffect, useState } from 'react';
import { api } from './api/cliente';

/**
 * Etiquetas y colores de los estatus, tal como están en el catálogo.
 *
 * La clave del estatus la fija el motor de estados y no cambia; cómo se
 * ve —el texto y el color— lo administra el usuario. Por eso no vive en
 * el código: quien administre catálogos puede renombrar «Cierre admin» a
 * «Cerrada» o cambiar un color sin que nadie despliegue nada.
 *
 * Se pide una sola vez por sesión y se guarda en memoria del módulo. Son
 * treinta filas que no cambian en toda la jornada, y pedirlas en cada
 * pantalla que muestre un estatus sería una petición por tabla.
 */

export interface OpcionEstatus {
  clave: string;
  nombre: string;
  color: string | null;
}

type Familias = Record<string, OpcionEstatus[]>;

let cache: Familias | null = null;
let enVuelo: Promise<Familias> | null = null;
const suscriptores = new Set<(f: Familias) => void>();

function cargar(): Promise<Familias> {
  if (cache) return Promise.resolve(cache);
  enVuelo ??= api.get<Familias>('/catalogos/etiquetas')
    .then((f) => {
      cache = f;
      for (const avisar of suscriptores) avisar(f);
      return f;
    })
    .catch(() => {
      // Sin catálogo se sigue trabajando: cada distintivo cae a su texto
      // crudo y su estilo neutro. Un color perdido no puede tumbar una
      // pantalla de órdenes.
      enVuelo = null;
      return {};
    });
  return enVuelo;
}

/** Se llama al abrir sesión: deja las etiquetas listas antes de la primera tabla. */
export function precargarEtiquetas(): void {
  void cargar();
}

/** Se llama al cerrar sesión: otra cuenta puede ver otros catálogos. */
export function olvidarEtiquetas(): void {
  cache = null;
  enVuelo = null;
}

export function useEtiquetas(familia: string): Map<string, OpcionEstatus> {
  const [familias, setFamilias] = useState<Familias>(cache ?? {});

  useEffect(() => {
    if (cache) {
      setFamilias(cache);
      return undefined;
    }
    suscriptores.add(setFamilias);
    void cargar();
    return () => { suscriptores.delete(setFamilias); };
  }, []);

  const mapa = new Map<string, OpcionEstatus>();
  for (const opcion of familias[familia] ?? []) mapa.set(opcion.clave, opcion);
  return mapa;
}

/**
 * El distintivo de un estatus.
 *
 * El color va en la letra y no de fondo. Una columna de píldoras
 * rellenas convierte la bandeja en un semáforo: el color pesa más que el
 * folio, la sucursal y la fecha, que es lo que en realidad se está
 * leyendo. Coloreado el texto, el estatus se distingue de un vistazo sin
 * pelearse con el resto de la fila.
 *
 * El respaldo importa: una clave nueva en el enum que nadie dio de alta
 * en el catálogo tiene que verse igual, no desaparecer.
 */
export function Distintivo({
  familia, valor, respaldo,
}: {
  familia: string;
  valor: string | null;
  /** Texto a usar si el catálogo no conoce esa clave. */
  respaldo?: string;
}) {
  const etiquetas = useEtiquetas(familia);
  if (!valor) return <span>—</span>;

  const opcion = etiquetas.get(valor);
  const texto = opcion?.nombre ?? respaldo ?? valor.replace(/_/g, ' ');

  if (!opcion?.color) return <span>{texto}</span>;

  return (
    <span style={{ color: opcion.color, fontWeight: 600 }}>{texto}</span>
  );
}
